import {
    CollectionDataLoaded,
    ContentDataTypes,
    DataLoaderGlobalParams,
    useOnScreen,
} from '@news-mono/web-common'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import React, { RefObject, useEffect, useRef, useState } from 'react'
import {
    LoadedData,
    useLoadMore,
} from '../../../data-controllers/LoadMore/useLoadMore'
import { StyledButtonContainer } from '../../../data-controllers/LoadMore/LoadMore.styled'
import { LoadingEllipsis } from '../../../LoadingEllipsis'
import {
    CurrentPageStyled,
    PaginationLinkStyled,
    PaginationLinksWrapperStyled,
} from './SixCardCollection.styled'
import { useMetadata } from './useMetaData'

export interface LoadMoreRouteProps {
    loadMoreEnabled?: boolean
}

interface LoadMoreProps {
    dataDefinition: ContentDataTypes
    data: MaybeLoaded<CollectionDataLoaded>
    onLoadMore: (data: LoadedData) => void
    services: DataLoaderGlobalParams
    loadMoreEnabled: boolean
    render: (
        data: MaybeLoaded<CollectionDataLoaded>,
    ) => JSX.Element | JSX.Element[]
    isVisible?: boolean
    ref?: RefObject<HTMLDivElement> | null
    currentPage?: number | null
    setCurrentPage: any
    currentUrl?: string
    pathname?: string
}

export const LoadMoreCards = ({
    dataDefinition: initialDataDefinition,
    data: initialData,
    onLoadMore,
    services,
    loadMoreEnabled,
    render,
    currentPage,
    setCurrentPage,
    currentUrl,
    pathname,
}: LoadMoreProps) => {
    const { loadMore, isLoading, data, dataDefinition } = useLoadMore(
        initialDataDefinition,
        initialData,
        services,
        onLoadMore,
    )
    const isLocalEnv = currentUrl?.includes('localhost')
    const http = isLocalEnv ? 'http' : 'https'
    const ref = useRef<HTMLDivElement>(null)

    const isInViewport = useOnScreen(ref)

    const prevLink =
        (currentPage as number) > 1
            ? `${http}://${currentUrl}${pathname}?page=${
                  (currentPage as number) - 1
              }`
            : null

    const nextLink = `${http}://${currentUrl}${pathname}?page=${
        (currentPage as number) + 1
    }`

    const canonicalUrl = `${http}://${currentUrl}${pathname}?page=${currentPage}`

    useEffect(() => {
        if (
            isInViewport &&
            loadMoreEnabled &&
            data.loaded &&
            data.result.loadMorePossible &&
            dataDefinition &&
            dataDefinition.type === 'listing' &&
            dataDefinition.paging &&
            !isLoading
        ) {
            loadMore(data)
        }
    }, [
        isInViewport,
        data,
        loadMore,
        dataDefinition,
        loadMoreEnabled,
        isLoading,
        setCurrentPage,
        currentUrl,
    ])

    function loadMoreButton() {
        if (
            loadMoreEnabled &&
            data.loaded &&
            data.result.loadMorePossible &&
            dataDefinition &&
            dataDefinition.type === 'listing' &&
            dataDefinition.paging
        ) {
            return (
                <>
                    <StyledButtonContainer ref={ref}>
                        {isInViewport && isLoading ? (
                            <LoadingEllipsis color="grey" />
                        ) : null}
                    </StyledButtonContainer>
                    <noscript>
                        <PaginationLinksWrapperStyled>
                            {prevLink && (
                                <PaginationLinkStyled
                                    href={prevLink}
                                    onClick={(e) => {
                                        e.preventDefault()
                                    }}
                                >
                                    Previous
                                </PaginationLinkStyled>
                            )}
                            <CurrentPageStyled>{currentPage}</CurrentPageStyled>
                            {nextLink && (
                                <PaginationLinkStyled
                                    href={nextLink}
                                    onClick={(e) => {
                                        e.preventDefault()
                                    }}
                                >
                                    Next
                                </PaginationLinkStyled>
                            )}
                        </PaginationLinksWrapperStyled>
                    </noscript>
                </>
            )
        }
        return null
    }

    useMetadata({ canonicalUrl, prevLink, nextLink })

    return (
        <>
            {render(data)}
            {loadMoreButton()}
        </>
    )
}
